import { TSchema } from '@sinclair/typebox';
import { Value } from '@sinclair/typebox/value';

export class ValidationError extends Error {
  constructor(
    message: string,
    public errors: unknown[],
    public data: unknown
  ) {
    super(message);
    this.name = 'ValidationError';
  }
}

export interface ValidationResult<T> {
  success: boolean;
  data: T | null;
  errors: unknown[];
}

/**
 * Universal schema validation - can throw or return result based on throwOnError flag
 */
export function validateBySchema<T>(
  schema: TSchema,
  data: unknown,
  throwOnError: boolean = true
): T | ValidationResult<T> {
  const errors = [...Value.Errors(schema, data)];

  if (errors.length > 0) {
    const schemaName = schema.$id || 'unnamed schema';
    const errorMessage = `Validation failed for ${schemaName}: ${errors.map(e => `${e.path}: ${e.message}`).join(', ')}`;

    if (throwOnError) {
      // Client behavior - fail fast
      throw new ValidationError(errorMessage, errors, data);
    } else {
      // Server behavior - return result
      return {
        success: false,
        data: null,
        errors,
      } as ValidationResult<T>;
    }
  }

  if (throwOnError) {
    return data as T;
  } else {
    return {
      success: true,
      data: data as T,
      errors: [],
    } as ValidationResult<T>;
  }
}
